import { useEffect, useRef, useState, useCallback } from 'react';
import { bookingsAPI, facilitiesAPI, configurationsAPI } from '../../services/api';
import {
  transformBookingFromAPI,
  transformSlotConfigFromAPI,
  transformIndividualSportsPricesFromAPI,
  loadFromStorage,
  scopedKey,
  resolveFacilityScopeId,
  pickFacilityRecord,
} from './bookingUtils';

/**
 * Loads and caches booking-domain data for the active facility.
 */
export function useBookingDataLoader({ dispatch, useAPI, isAuthenticated, authLoading, activeFacilityId }) {
  const [loading, setLoading] = useState(true);
  const prevFacilityIdRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    const facilityScopeId = resolveFacilityScopeId(activeFacilityId);
    const bookingsKey = scopedKey('topgame_bookings', facilityScopeId);
    const facilityKey = scopedKey('topgame_facility', facilityScopeId);
    const slotConfigKey = scopedKey('topgame_slotConfiguration', facilityScopeId);

    if (
      useAPI &&
      prevFacilityIdRef.current !== null &&
      facilityScopeId != null &&
      Number(prevFacilityIdRef.current) !== Number(facilityScopeId)
    ) {
      dispatch({ type: 'LOAD_BOOKINGS', payload: [] });
      dispatch({ type: 'UPDATE_SLOT_CONFIGURATION', payload: { dateRange: null, services: [] } });
    }
    prevFacilityIdRef.current = facilityScopeId ?? null;

    if (useAPI) {
      try {
        let facilityServices = [];
        let servicesList = [];

        const bookingsRes = await bookingsAPI.getAll();
        if (bookingsRes.success && bookingsRes.data) {
          let transformedBookings = Array.isArray(bookingsRes.data)
            ? bookingsRes.data.map(transformBookingFromAPI)
            : [transformBookingFromAPI(bookingsRes.data)];

          if (facilityScopeId != null) {
            transformedBookings = transformedBookings.filter((b) => {
              if (b.facilityId == null) return true;
              return Number(b.facilityId) === Number(facilityScopeId);
            });
          }

          if (Array.isArray(transformedBookings) && transformedBookings.length > 0) {
            dispatch({ type: 'LOAD_BOOKINGS', payload: transformedBookings });
          } else {
            const localBookings = loadFromStorage(bookingsKey, []);
            dispatch({
              type: 'LOAD_BOOKINGS',
              payload: Array.isArray(localBookings) && localBookings.length > 0 ? localBookings : [],
            });
          }
        }

        const facilityRes = await facilitiesAPI.get();
        if (facilityRes.success && facilityRes.data) {
          const baseFacility = pickFacilityRecord(facilityRes.data, facilityScopeId);
          try {
            const servicesRes = await facilitiesAPI.getServices(null, facilityScopeId != null ? facilityScopeId : null);
            servicesList = servicesRes && servicesRes.success && Array.isArray(servicesRes.data) ? servicesRes.data : [];
            if (facilityScopeId != null && servicesList.length > 0) {
              servicesList = servicesList.filter(
                (s) => s.facility_id != null && Number(s.facility_id) === Number(facilityScopeId)
              );
            }
            const baseFacilityServices = Array.isArray(baseFacility?.services) ? baseFacility.services : [];
            facilityServices = servicesRes.success && Array.isArray(servicesRes.data) ? servicesList : baseFacilityServices;
            dispatch({
              type: 'LOAD_FACILITY',
              payload: { ...(baseFacility || {}), services: facilityServices },
            });
          } catch (servicesError) {
            console.error('Error loading services:', servicesError);
            dispatch({ type: 'LOAD_FACILITY', payload: baseFacility || facilityRes.data });
          }
        }

        let groundsList = [];
        try {
          const groundsRes = await facilitiesAPI.getGrounds();
          const rawGrounds = Array.isArray(groundsRes?.data) ? groundsRes.data : [];
          groundsList =
            facilityScopeId != null
              ? rawGrounds.filter((g) => Number(g.facility_id) === Number(facilityScopeId))
              : rawGrounds;
        } catch (e) {
          console.error('Error loading grounds:', e);
        }

        if (groundsList.length > 0) {
          const groundsWithServices = groundsList.map((g) => {
            const gid = parseInt(g.id, 10);
            const attachedServices =
              servicesList.length > 0
                ? servicesList.filter((s) => parseInt(s.ground_id, 10) === gid)
                : Array.isArray(g.services)
                  ? g.services
                  : [];
            return { ...g, services: attachedServices };
          });
          dispatch({ type: 'LOAD_GROUNDS', payload: groundsWithServices });
        } else {
          dispatch({ type: 'LOAD_GROUNDS', payload: [] });
        }

        try {
          const configRes = await configurationsAPI.getAll();
          let slotRows =
            configRes?.success && Array.isArray(configRes?.data?.slotConfigurations) ? configRes.data.slotConfigurations : [];
          if (facilityScopeId != null && slotRows.length > 0) {
            slotRows = slotRows.filter(
              (r) => r.ground_facility_id == null || Number(r.ground_facility_id) === Number(facilityScopeId)
            );
          }
          let individualRows =
            configRes?.success && Array.isArray(configRes?.data?.individualSportsPrices)
              ? configRes.data.individualSportsPrices
              : [];
          if (facilityScopeId != null && individualRows.length > 0) {
            individualRows = individualRows.filter(
              (r) => r.ground_facility_id == null || Number(r.ground_facility_id) === Number(facilityScopeId)
            );
          }

          const slotConfig = transformSlotConfigFromAPI(slotRows);
          const individualConfigs = transformIndividualSportsPricesFromAPI(individualRows, facilityServices);
          const configMap = new Map();

          [...(slotConfig.services || []), ...individualConfigs].forEach((config) => {
            const service = config?.service || {};
            const scfg = config?.slotConfigurationId;
            const key =
              service.id != null
                ? `id:${service.id}`
                : scfg != null
                  ? `scfg:${scfg}`
                  : `name:${service.name || ''}|type:${service.type || ''}`;

            if (!configMap.has(key)) {
              configMap.set(key, config);
            } else {
              const existing = configMap.get(key);
              configMap.set(key, {
                ...existing,
                ...config,
                service: { ...(existing?.service || {}), ...(config?.service || {}) },
                slots: config?.slots || existing?.slots || [],
                priceSetup: { ...(existing?.priceSetup || {}), ...(config?.priceSetup || {}) },
              });
            }
          });

          const mergedConfig = {
            dateRange: slotConfig.dateRange || null,
            services: Array.from(configMap.values()),
          };

          if (mergedConfig.services.length > 0 || mergedConfig.dateRange) {
            dispatch({ type: 'UPDATE_SLOT_CONFIGURATION', payload: mergedConfig });
          } else {
            const localSlotConfig = loadFromStorage(slotConfigKey, { dateRange: null, services: [] });
            if (localSlotConfig && (localSlotConfig.services?.length > 0 || localSlotConfig.dateRange)) {
              dispatch({ type: 'UPDATE_SLOT_CONFIGURATION', payload: localSlotConfig });
            }
          }
        } catch (configError) {
          console.error('Error loading configurations from API, using localStorage:', configError);
          const localSlotConfig = loadFromStorage(slotConfigKey, { dateRange: null, services: [] });
          if (localSlotConfig && (localSlotConfig.services?.length > 0 || localSlotConfig.dateRange)) {
            dispatch({ type: 'UPDATE_SLOT_CONFIGURATION', payload: localSlotConfig });
          }
        }
      } catch (error) {
        console.error('Error loading data from API, falling back to localStorage:', error);
        dispatch({ type: 'LOAD_BOOKINGS', payload: loadFromStorage(bookingsKey, []) });
        dispatch({ type: 'LOAD_FACILITY', payload: loadFromStorage(facilityKey, { name: '', description: '', services: [] }) });
        const slotConfig = loadFromStorage(slotConfigKey, { dateRange: null, services: [] });
        if (slotConfig && (slotConfig.services?.length > 0 || slotConfig.dateRange)) {
          dispatch({ type: 'UPDATE_SLOT_CONFIGURATION', payload: slotConfig });
        }
      }
    } else {
      dispatch({ type: 'LOAD_BOOKINGS', payload: loadFromStorage(bookingsKey, []) });
      dispatch({ type: 'LOAD_FACILITY', payload: loadFromStorage(facilityKey, { name: '', description: '', services: [] }) });
      const slotConfig = loadFromStorage(slotConfigKey, { dateRange: null, services: [] });
      if (slotConfig && (slotConfig.services?.length > 0 || slotConfig.dateRange)) {
        dispatch({ type: 'UPDATE_SLOT_CONFIGURATION', payload: slotConfig });
      }
    }
    setLoading(false);
  }, [dispatch, useAPI, activeFacilityId]);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      prevFacilityIdRef.current = null;
      dispatch({ type: 'LOAD_BOOKINGS', payload: [] });
      dispatch({ type: 'LOAD_FACILITY', payload: { name: '', description: '', services: [] } });
      dispatch({ type: 'UPDATE_SLOT_CONFIGURATION', payload: { dateRange: null, services: [] } });
      dispatch({ type: 'LOAD_GROUNDS', payload: [] });
      setLoading(false);
      return;
    }

    loadData();
  }, [useAPI, isAuthenticated, authLoading, activeFacilityId, loadData, dispatch]);

  return { loading, reloadBookings: loadData };
}
